import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { authApi } from '@/lib/api'
import type { ReferralOverview, TopReferrer } from '@/lib/referral-types'
import { formatCents, formatPct } from '@/lib/referral-utils'
import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import { DataTable, type Column } from '@/components/ui/DataTable'
import { PiiField } from '@/components/ui/PiiField'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { usePresentation } from '@/components/layout/AppShell'

export function ReferralOverviewPage() {
  const navigate = useNavigate()
  const { presenting } = usePresentation()

  const { data, isLoading } = useQuery({
    queryKey: ['admin-referral-overview'],
    queryFn: () => authApi.get<ReferralOverview>('/api/v1/admin/referral/overview'),
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24"><LoadingSpinner size="lg" /></div>
    )
  }

  const d = data

  const funnelSteps = d ? [
    { label: 'Total Referred', value: d.total_referrals ?? 0 },
    { label: 'Active (Trial + Converted)', value: d.active_referrals ?? 0 },
    { label: 'Converted', value: d.conversions ?? 0 },
    { label: 'Churned', value: d.churned ?? 0 },
  ] : []

  const columns: Column<TopReferrer>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (row) => <PiiField value={row.name} presenting={presenting} className="font-medium" />,
    },
    {
      id: 'email',
      header: 'Email',
      cell: (row) => <PiiField value={row.email} presenting={presenting} />,
    },
    {
      id: 'referral_count',
      header: 'Referrals',
      cell: (row) => <span className="tabular-nums">{row.referral_count}</span>,
      className: 'text-right',
    },
    {
      id: 'conversion_count',
      header: 'Conversions',
      cell: (row) => <span className="tabular-nums">{row.conversion_count}</span>,
      className: 'text-right',
    },
    {
      id: 'earned_cents',
      header: 'Earned',
      cell: (row) => <span className="tabular-nums">{formatCents(row.earned_cents)}</span>,
      className: 'text-right',
    },
  ]

  return (
    <div>
      <h1 className="text-2xl font-heading font-semibold mb-6">Referral Overview</h1>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <KpiCard label="Total Referrals" value={d?.total_referrals?.toLocaleString() ?? '0'} />
        <KpiCard label="Active" value={d?.active_referrals?.toLocaleString() ?? '0'} />
        <KpiCard label="Conversion Rate" value={d ? formatPct(d.conversion_rate) : '0%'} />
        <KpiCard label="Total Issued" value={formatCents(d?.total_issued_cents)} />
        <KpiCard label="Pending" value={formatCents(d?.total_pending_cents)} />
        <KpiCard label="Clawbacks" value={formatCents(d?.total_clawbacks_cents)} />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {/* Referral funnel */}
        {funnelSteps.length > 0 && (
          <Card>
            <CardHeader>
              <h3 className="font-heading font-semibold text-sm">Referral Funnel</h3>
            </CardHeader>
            <CardContent className="space-y-3">
              {funnelSteps.map((step, _i, arr) => {
                const maxVal = arr[0]!.value
                const pct = maxVal > 0 ? (step.value / maxVal) * 100 : 0
                return (
                  <div key={step.label}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[var(--content-secondary)]">{step.label}</span>
                      <span className="tabular-nums font-medium">{step.value.toLocaleString()}</span>
                    </div>
                    <div className="h-3 bg-[var(--surface-nested)] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#65A0A8] rounded-full transition-all"
                        style={{ width: `${Math.max(2, pct)}%` }}
                      />
                    </div>
                  </div>
                )
              })}
            </CardContent>
          </Card>
        )}

        {/* Quick stats card */}
        <Card>
          <CardHeader>
            <h3 className="font-heading font-semibold text-sm">Reward Breakdown</h3>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              { label: 'Issued (Credited + Paid)', value: d?.total_issued_cents ?? 0, color: 'bg-emerald-500' },
              { label: 'Pending Approval', value: d?.total_pending_cents ?? 0, color: 'bg-amber-500' },
              { label: 'Clawed Back', value: d?.total_clawbacks_cents ?? 0, color: 'bg-red-500' },
            ].map((item) => {
              const total = (d?.total_issued_cents ?? 0) + (d?.total_pending_cents ?? 0) + (d?.total_clawbacks_cents ?? 0)
              const pct = total > 0 ? (item.value / total) * 100 : 0
              return (
                <div key={item.label}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[var(--content-secondary)]">{item.label}</span>
                    <span className="tabular-nums">{formatCents(item.value)}</span>
                  </div>
                  <div className="h-3 bg-[var(--surface-nested)] rounded-full overflow-hidden">
                    <div className={`h-full ${item.color} rounded-full transition-all`} style={{ width: `${Math.max(1, pct)}%` }} />
                  </div>
                </div>
              )
            })}
          </CardContent>
        </Card>
      </div>

      {/* Top referrers */}
      {d?.top_referrers && d.top_referrers.length > 0 && (
        <Card>
          <CardHeader>
            <h3 className="font-heading font-semibold text-sm">Top Referrers</h3>
          </CardHeader>
          <DataTable
            columns={columns}
            data={d.top_referrers}
            keyExtractor={(row) => row.id}
            onRowClick={(row) => navigate(`/users/${row.id}`)}
            emptyMessage="No referrers yet"
          />
        </Card>
      )}
    </div>
  )
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="py-3">
        <p className="text-xs text-[var(--content-tertiary)] uppercase tracking-wider mb-1">{label}</p>
        <p className="text-xl font-heading font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  )
}
